import type { CmmdCampaign, CmmdProject } from "@cmmd-forge/contracts";

export const TIMELINE_MIN_WIDTH_PX = 920;
export const ROW_HEIGHT_PX = 40;

// Jira-style timeline zoom levels. The grid is percentage-based, so a scale
// change just regenerates the columns and rescales the total pixel width.
export type TimelineScale = "weeks" | "months" | "quarters";

// Target px per column at each scale — drives the timeline's total width (zoom).
export const COLUMN_DENSITY_PX: Record<TimelineScale, number> = {
  weeks: 48,
  months: 132,
  quarters: 232,
};

// `TimelineMonth` is the generic column shape (one cell at the active scale —
// a week, month, or quarter). Kept the name to avoid churn across renderers.
export interface TimelineMonth {
  readonly label: string;
  readonly key: string;
  readonly start: Date;
  readonly end: Date;
}

export interface TimelineLane {
  readonly kind: string;
  readonly id: string | null;
  readonly title: string;
  readonly campaign?: CmmdCampaign | null;
  readonly projects: ReadonlyArray<CmmdProject>;
}

export function parseDate(raw: string | null | undefined): Date | null {
  if (!raw) return null;
  const d = new Date(raw);
  return isNaN(d.getTime()) ? null : d;
}

export function clamp(d: Date, min: Date, max: Date): Date {
  if (d < min) return min;
  if (d > max) return max;
  return d;
}

export function generateMonths(start: Date, end: Date): TimelineMonth[] {
  const months: TimelineMonth[] = [];
  const cursor = new Date(start.getFullYear(), start.getMonth(), 1);
  const endMonth = new Date(end.getFullYear(), end.getMonth(), 1);
  while (cursor.getTime() <= endMonth.getTime()) {
    const monthStart = new Date(cursor);
    const monthEnd = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1);
    months.push({
      label: cursor.toLocaleString("default", { month: "short" }),
      key: `${cursor.getFullYear()}-${cursor.getMonth()}`,
      start: monthStart,
      end: monthEnd,
    });
    cursor.setMonth(cursor.getMonth() + 1);
  }
  return months;
}

function startOfWeek(d: Date): Date {
  const date = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  date.setDate(date.getDate() - date.getDay()); // back to Sunday
  return date;
}

export function generateWeeks(start: Date, end: Date): TimelineMonth[] {
  const weeks: TimelineMonth[] = [];
  const cursor = startOfWeek(start);
  while (cursor.getTime() <= end.getTime()) {
    const weekStart = new Date(cursor);
    const weekEnd = new Date(cursor.getFullYear(), cursor.getMonth(), cursor.getDate() + 7);
    weeks.push({
      label: weekStart.toLocaleString("default", { month: "short", day: "numeric" }),
      key: `w-${weekStart.getFullYear()}-${weekStart.getMonth()}-${weekStart.getDate()}`,
      start: weekStart,
      end: weekEnd,
    });
    cursor.setDate(cursor.getDate() + 7);
  }
  return weeks;
}

export function generateQuarters(start: Date, end: Date): TimelineMonth[] {
  const quarters: TimelineMonth[] = [];
  const cursor = new Date(start.getFullYear(), Math.floor(start.getMonth() / 3) * 3, 1);
  const endQuarter = new Date(end.getFullYear(), Math.floor(end.getMonth() / 3) * 3, 1);
  while (cursor.getTime() <= endQuarter.getTime()) {
    const quarterStart = new Date(cursor);
    const quarterEnd = new Date(cursor.getFullYear(), cursor.getMonth() + 3, 1);
    const quarter = Math.floor(cursor.getMonth() / 3) + 1;
    quarters.push({
      label: `Q${quarter} '${String(cursor.getFullYear()).slice(2)}`,
      key: `q-${cursor.getFullYear()}-${quarter}`,
      start: quarterStart,
      end: quarterEnd,
    });
    cursor.setMonth(cursor.getMonth() + 3);
  }
  return quarters;
}

export function generateColumns(start: Date, end: Date, scale: TimelineScale): TimelineMonth[] {
  if (scale === "weeks") return generateWeeks(start, end);
  if (scale === "quarters") return generateQuarters(start, end);
  return generateMonths(start, end);
}

export function formatRange(start: Date | null, end: Date | null): string {
  const formatter = new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric" });
  if (start && end) return `${formatter.format(start)} - ${formatter.format(end)}`;
  if (start) return `Starts ${formatter.format(start)}`;
  if (end) return `Due ${formatter.format(end)}`;
  return "No dates";
}

interface TimelineGridProps {
  readonly months: ReadonlyArray<TimelineMonth>;
  readonly axisStart: Date;
  readonly axisEnd: Date;
  readonly rowCount: number;
  readonly toPercent: (date: Date) => number;
}

export function TimelineGrid({
  months,
  axisStart,
  axisEnd,
  rowCount,
  toPercent,
}: TimelineGridProps) {
  const today = new Date();
  const todayVisible = today >= axisStart && today <= axisEnd;

  return (
    <div className="pointer-events-none absolute inset-0">
      <div className="sticky top-0 z-10 grid h-11 border-b border-border bg-background/95 backdrop-blur">
        {months.map((month) => {
          const leftPct = toPercent(month.start);
          const rightPct = toPercent(month.end);
          return (
            <div
              key={month.key}
              className="absolute top-0 bottom-0 border-l border-border px-3 py-2 first:border-l-0"
              style={{ left: `${leftPct}%`, width: `${Math.max(rightPct - leftPct, 8)}%` }}
            >
              <span className="text-[11px] font-medium text-foreground">{month.label}</span>
            </div>
          );
        })}
      </div>
      <div className="relative" style={{ height: rowCount * ROW_HEIGHT_PX }}>
        {months.map((month) => (
          <div
            key={`${month.key}-line`}
            className="absolute top-0 bottom-0 border-l border-border"
            style={{ left: `${toPercent(month.start)}%` }}
          />
        ))}
        {Array.from({ length: rowCount }, (_, index) => (
          <div
            key={index}
            className="absolute right-0 left-0 border-b border-border"
            style={{ top: (index + 1) * ROW_HEIGHT_PX }}
          />
        ))}
        {todayVisible ? (
          <div
            className="absolute top-0 bottom-0 z-20 w-px bg-primary"
            style={{ left: `${toPercent(today)}%` }}
          >
            {/* Solid pill, centered on the line, so it cleanly occludes the
                column-header label behind it instead of letting it bleed through. */}
            <span className="absolute -top-8 left-1/2 -translate-x-1/2 rounded bg-primary px-1.5 py-0.5 text-[10px] font-medium whitespace-nowrap text-primary-foreground shadow-sm">
              Today
            </span>
          </div>
        ) : null}
      </div>
    </div>
  );
}
